/* eslint-disable @typescript-eslint/no-explicit-any */
import { Box, FormHelperText } from '@mui/material'
import { useMutation, useQuery } from '@tanstack/react-query'
import { customerApis } from 'api'
import {
  CUSTOMER_DETAILS,
  CUSTOMER_REPORT,
  CUSTOMER_REPORT_ALL,
  GIFT_TRANSACTION_HISTORY,
  TEM_ACCUMULATIONS,
} from 'constans/queryKeys'
import useToastify from 'hooks/useToastify'
import { useCallback, useState } from 'react'
import { ApiResponseModel, DateRangeQuery } from 'types/common'
import { CustomerModel, CustomerRequestModel } from 'types/customers'
import CustomerInfo from './CustomerInfo'
import GiftExchangeHistories from './GiftExchangeHistories'
import SearchCustomer from './SearchCustomer'
import TemAccumulationHistories from './TemAccumulationHistories'

const getDefaultDateRange = () => {
  const fromDate = new Date()
  fromDate.setMonth(fromDate.getMonth() - 3)
  const toDate = new Date()
  return {
    fromDate: new Date(fromDate).toISOString(),
    toDate: new Date(toDate).toISOString(),
  }
}

const Customer = () => {
  const [phoneNumber, setPhoneNumber] = useState('')
  const [dateRange, setDateRange] = useState<DateRangeQuery>(
    getDefaultDateRange()
  )

  const [customerData, setCustomerData] = useState<CustomerModel | undefined>()
  const [customerNotFound, setCustomerNotFound] = useState(false)
  const { showError } = useToastify()
  const { mutateAsync, isPending } = useMutation({
    mutationKey: [CUSTOMER_DETAILS],
    mutationFn: (payload: CustomerRequestModel) =>
      customerApis.getCustomer({
        phoneNumber: payload.phoneNumber,
      }),
  })

  const handleDownload = useCallback((linkDownload: string) => {
    const link = document.createElement('a')
    link.href = linkDownload
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }, [])

  const { mutateAsync: exportAll, isPending: isExportAllPending } = useMutation(
    {
      mutationKey: [CUSTOMER_REPORT_ALL],
      mutationFn: (payload: DateRangeQuery) =>
        customerApis.exportAllReport(payload),
      onSuccess: (d: ApiResponseModel<string>) => {
        if (d?.ErrorCode === 1 || !d?.Result) {
          showError(
            d?.ErrorMessage || 'Vui lòng kiểm tra số điện thoại đã nhập!'
          )
        } else {
          if (d?.Result) {
            handleDownload(d.Result)
          }
        }
      },
      onError: () => {
        showError('Hệ thống đã xảy ra lỗi, vui lòng thử lại')
      },
    }
  )

  const { mutate: exportReport, isPending: isExporting } = useMutation({
    mutationKey: [CUSTOMER_REPORT],
    mutationFn: (val: string) => customerApis.exportReport(val),
    onSuccess: (d: ApiResponseModel<string>) => {
      if (d?.ErrorCode === 1 || !d?.Result) {
        showError(d?.ErrorMessage || 'Vui lòng kiểm tra số điện thoại đã nhập!')
      } else {
        handleDownload(d?.Result)
      }
    },
    onError: () => {
      showError('Hệ thống đã xảy ra lỗi, vui lòng thử lại')
    },
  })

  const { data: giftTransactions, refetch: refetchGifts } = useQuery({
    queryKey: [GIFT_TRANSACTION_HISTORY],
    queryFn: () =>
      customerApis.giftTransactions({
        customerPhone: phoneNumber || '',
      }),
    enabled: false,
  })

  const { data: temTransactions, refetch: refetchTems } = useQuery({
    queryKey: [TEM_ACCUMULATIONS],
    queryFn: () =>
      customerApis.getTemAccumulation({
        customerPhone: phoneNumber || '',
        transactionType: -1,
      }),
    enabled: false,
  })

  const handleSetPhoneNumber = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setPhoneNumber(e.target.value)
      setCustomerNotFound(false)
    },
    []
  )

  const handleSearch = useCallback(async () => {
    await mutateAsync(
      { phoneNumber },
      {
        onSuccess: (res: ApiResponseModel<CustomerModel>) => {
          if (res.ErrorCode === 1) {
            showError(
              res.ErrorMessage || 'Đã xảy ra lỗi, vui lòng thử lại sau!'
            )
          } else {
            const result =
              res && res.Result && Array.isArray(res.Result)
                ? res.Result[0]
                : res.Result
            setCustomerData(result)
            setCustomerNotFound(!result)
            if (result) {
              refetchGifts()
              refetchTems()
            }
          }
        },
        onError: () => {
          showError('Đã xảy ra lỗi, vui lòng thử lại sau!')
        },
      }
    )
  }, [mutateAsync, phoneNumber, refetchGifts, refetchTems, showError])

  const handleExportReport = useCallback(async () => {
    if (customerData?.Mobile !== phoneNumber) {
      await mutateAsync(
        { phoneNumber },
        {
          onSuccess: (res: ApiResponseModel<CustomerModel>) => {
            if (res.ErrorCode === 1) {
              showError(
                res.ErrorMessage || 'Đã xảy ra lỗi, vui lòng thử lại sau!'
              )
            } else {
              const result =
                res && res.Result && Array.isArray(res.Result)
                  ? res.Result[0]
                  : res.Result
              setCustomerData(result)
              setCustomerNotFound(!result)
              if (result) {
                refetchGifts()
                refetchTems()
                exportReport(phoneNumber)
              }
            }
          },
          onError: () => {
            showError('Đã xảy ra lỗi, vui lòng thử lại sau!')
          },
        }
      )
    } else {
      exportReport(phoneNumber)
    }
  }, [
    mutateAsync,
    phoneNumber,
    refetchGifts,
    refetchTems,
    showError,
    exportReport,
    customerData,
  ])

  const handleExportAll = useCallback(() => {
    if (!!dateRange.fromDate && !!dateRange.toDate) {
      exportAll(dateRange)
    }
  }, [exportAll, dateRange])

  const onDateRangeChange = useCallback((range: DateRangeQuery) => {
    setDateRange({
      fromDate: range.fromDate,
      toDate: range.toDate,
    })
  }, [])

  return (
    <div className="content">
      <Box display="flex" flexDirection="column" gap={6}>
        <Box>
          <SearchCustomer
            onChangePhoneNumber={handleSetPhoneNumber}
            onSearch={handleSearch}
            disabled={!phoneNumber || isPending}
            loading={isPending}
            onReport={handleExportReport}
            isExporting={isExporting}
            disabledReport={!phoneNumber || isExporting || !customerData}
            onExportAll={handleExportAll}
            isExportingAll={isExportAllPending}
            onDateRangeChange={onDateRangeChange}
            dateRangeValue={dateRange}
            defaultDateRange={getDefaultDateRange()}
          />
          {customerNotFound && (
            <FormHelperText
              sx={{
                fontWeight: 500,
                pl: '116px',
              }}
              error
            >
              Không tìm thấy khách hàng!
            </FormHelperText>
          )}
        </Box>
        <CustomerInfo data={customerData ? [customerData] : []} />
        <GiftExchangeHistories
          data={customerData ? giftTransactions : undefined}
        />
        <TemAccumulationHistories
          data={customerData ? temTransactions : undefined}
        />
      </Box>
    </div>
  )
}

export default Customer
